// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const STILLNESS_TIMEOUT_MS = 50;
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const MIN_PIXELS_PER_MS = 0.2;
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const VELOCITY_SAMPLE_TIME_MS = 40;

type Point = { x: number; y: number };

interface TrackMouseLeaveIntentOptions {
  initialMousePosition: Point;
  targetRect: DOMRect;
  onLeave: () => void;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(value, max));

const getDistanceToRect = (point: Point, rect: DOMRect): number => {
  const nearestX = clamp(point.x, rect.x, rect.x + rect.width);
  const nearestY = clamp(point.y, rect.y, rect.y + rect.height);

  const dx = point.x - nearestX;
  const dy = point.y - nearestY;

  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * The utility function calls onLeave when user is not intending to hover the element.
 * Specifically, the rules:
 * - If the mouse is moving away from the target rect, it will call onLeave
 * - If the mouse is moving towards the target rect, but with a slow velocity, it will also call onLeave
 */
const trackMouseLeaveIntent = ({
  initialMousePosition,
  targetRect,
  onLeave,
}: TrackMouseLeaveIntentOptions): (() => void) => {
  let lastPosition = initialMousePosition;
  let lastDistance = getDistanceToRect(initialMousePosition, targetRect);
  let stillnessTimeout: ReturnType<typeof setTimeout> | null = null;
  let hasCleanedUp = false;

  let stateSinceLastSample = {
    startedAt: Date.now(),
    distance: lastDistance,
  };

  const clearStillnessTimeout = () => {
    if (stillnessTimeout) {
      clearTimeout(stillnessTimeout);
      stillnessTimeout = null;
    }
  };

  const cleanup = () => {
    if (hasCleanedUp) return;
    hasCleanedUp = true;
    document.removeEventListener('mousemove', handleMouseMove);
    clearStillnessTimeout();
  };

  const handleLeave = () => {
    try {
      onLeave();
    } finally {
      cleanup();
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    clearStillnessTimeout();

    const position = { x: event.clientX, y: event.clientY };
    const distance = getDistanceToRect(position, targetRect);

    // Sometimes mousemove events are fired with the same position, so we need to check if the mouse has moved
    const hasMouseMoved =
      position.x !== lastPosition.x || position.y !== lastPosition.y;
    const isMouseMovingTowardsTarget = lastDistance - distance > 0;

    lastPosition = position;
    lastDistance = distance;

    if (hasMouseMoved && !isMouseMovingTowardsTarget) {
      // Quick exit if the mouse has not moved towards the target element
      handleLeave();
      return;
    }

    const now = Date.now();
    const elapsedTime = now - stateSinceLastSample.startedAt;
    if (elapsedTime >= VELOCITY_SAMPLE_TIME_MS) {
      if (
        (stateSinceLastSample.distance - distance) / elapsedTime <
        MIN_PIXELS_PER_MS
      ) {
        handleLeave();
        return;
      }

      stateSinceLastSample = { startedAt: now, distance };
    }

    stillnessTimeout = setTimeout(handleLeave, STILLNESS_TIMEOUT_MS);
  };

  stillnessTimeout = setTimeout(handleLeave, STILLNESS_TIMEOUT_MS);

  document.addEventListener('mousemove', handleMouseMove);
  return cleanup;
};

export default trackMouseLeaveIntent;
